import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import DashboardSidebar from '../DashboardSidebar';
import ActivityDrawer from '../ActivityDrawer';
import VatucoLogo from '../VatucoLogo';

function ResponsiveDashboardShell({
  activePath = '/',
  theme,
  children,
  mainClassName = '',
  contentClassName = '',
  contentMaxWidth,
  contentStyle,
  withContentWrapper = true,
  onSearch,
  headerSlot,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchValue, setSearchValue] = useState('');
  const [activityOpen, setActivityOpen] = useState(false);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
      if (onSearch) {
        onSearch(searchValue.trim());
      } else {
        navigate(`/?q=${encodeURIComponent(searchValue.trim())}`);
      }
    }
  };

  const mergedContentStyle = {
    ...(contentMaxWidth ? { maxWidth: contentMaxWidth } : null),
    ...contentStyle,
  };

  return (
    <div
      className="dashboard-new dashboard-shell font-body selection:bg-primary-container selection:text-on-primary-container flex flex-col h-screen overflow-hidden"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      {/* ── GLOBAL HEADER ── */}
      <header
        className="shrink-0 z-50 border-b"
        style={{ backgroundColor: 'var(--chrome-sidebar-bg)', borderColor: 'var(--chrome-border-subtle)' }}
      >
        <div className="flex items-center gap-3 px-4 tablet-sm:px-5 h-14">
          {/* Logo */}
          <a
            onClick={() => navigate('/')}
            className="shrink-0 cursor-pointer flex items-center gap-2 mr-2"
          >
            <VatucoLogo className="text-lg" primary="var(--chrome-strong)" />
          </a>

          {/* Search bar */}
          <form onSubmit={handleSearchSubmit} className="flex-1">
            <div className="flex items-center bg-[var(--chrome-hover-bg)] rounded-lg border border-[var(--chrome-border-subtle)] overflow-hidden focus-within:border-primary focus-within:ring-1 focus-within:ring-primary transition-all">
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={t('Buscar productos...')}
                className="flex-1 px-4 py-2.5 bg-transparent text-sm text-[var(--chrome-text)] placeholder-[var(--chrome-faint)] outline-none border-none"
              />
              <button
                type="submit"
                className="px-4 py-2.5 bg-primary text-on-primary flex items-center justify-center shrink-0 hover:opacity-90 transition-opacity"
              >
                <span className="material-symbols-outlined text-[20px]">search</span>
              </button>
            </div>
          </form>

          {/* Page-level filter slot (e.g. Dashboard category/community dropdowns) */}
          {headerSlot && (
            <div className="hidden tablet-sm:flex items-center gap-2 shrink-0">
              {headerSlot}
            </div>
          )}

          {/* Activity bell — hidden on desktop-3xl+ where the right sidebar shows */}
          {user && (
            <button
              onClick={() => setActivityOpen(true)}
              className="desktop-3xl:hidden ml-auto p-2 rounded-lg hover:bg-[var(--chrome-hover-bg)] transition-colors shrink-0"
              style={{ color: 'var(--chrome-muted)' }}
              title={t('Actividad')}
            >
              <span className="material-symbols-outlined text-[22px]">notifications</span>
            </button>
          )}
        </div>
      </header>

      {/* ── BODY: Sidebar + Main ── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <DashboardSidebar activePath={activePath} />

        <main
          className={`dashboard-shell__main hide-scrollbar flex-1 overflow-y-auto pb-20 tablet-sm:pb-0 ${mainClassName}`.trim()}
        >
          {withContentWrapper ? (
            <div
              className={`w-full mx-auto px-4 mobile-sm:px-6 tablet-md:px-8 pt-5 pb-8 max-w-[1400px] ${contentClassName}`.trim()}
              style={mergedContentStyle}
            >
              {children}
            </div>
          ) : (
            children
          )}
        </main>
      </div>

      {/* Activity drawer (small/medium screens) */}
      <ActivityDrawer open={activityOpen} onClose={() => setActivityOpen(false)} />
    </div>
  );
}

export default ResponsiveDashboardShell;
